<template>
  <el-dialog
    :title="dialogTitle"
    :visible="visible"
    width="680px"
    append-to-body
    :close-on-click-modal="false"
    @close="$emit('update:visible', false)"
  >
    <div v-loading="detailLoading">
      <el-form
        class="report-type-form"
        label-width="120px"
      >
        <el-form-item label="报告类型">
          <el-radio-group
            v-model="form.reportType"
            :disabled="typeLocked"
            @change="handleReportTypeChange"
          >
            <el-radio label="monthly">
              月报
            </el-radio>
            <el-radio label="weekly">
              周报
            </el-radio>
            <el-radio label="task">
              任务明细报告
            </el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <el-form
        v-if="isWorkSummary"
        ref="summaryForm"
        :model="form"
        :rules="summaryRules"
        label-width="120px"
      >
        <el-form-item
          v-if="form.reportType === 'monthly'"
          label="报告月份"
          prop="periodMonth"
        >
          <el-date-picker
            v-model="form.periodMonth"
            type="month"
            placeholder="选择月份"
            value-format="yyyy-MM"
            format="yyyy年MM月"
            :picker-options="monthPickerOptions"
            style="width: 220px"
            @change="handleMonthChange"
          />
          <div class="field-hint">
            仅可选择当前月份之前的完整自然月。
          </div>
        </el-form-item>
        <el-form-item
          v-if="form.reportType === 'weekly'"
          label="周报周次"
          prop="weekAnchorDate"
        >
          <el-date-picker
            v-model="form.weekAnchorDate"
            type="date"
            placeholder="选择周内任意一日"
            value-format="yyyy-MM-dd"
            format="yyyy-MM-dd"
            :picker-options="weeklyPickerOptions"
            style="width: 260px"
            @change="handleWeekAnchorChange"
          />
          <div
            v-if="weeklyRangeHint"
            class="field-hint"
          >
            {{ weeklyRangeHint }}
          </div>
          <div class="field-hint">
            系统将自动对齐为周日至周六，且仅可选择当前周之前的完整自然周。
          </div>
        </el-form-item>
        <el-form-item label="工作图片">
          <ImageUploader
            v-model="form.reportExt.workImages"
            :max="9"
            :show-zoom="true"
          />
        </el-form-item>
        <el-form-item
          v-if="form.reportType === 'weekly'"
          label="巡查保养总结"
        >
          <el-input
            v-model="form.reportExt.patrolSummary"
            type="textarea"
            :rows="4"
            maxlength="2000"
            show-word-limit
          />
        </el-form-item>
        <el-form-item
          v-if="form.reportType === 'weekly'"
          label="下周工作计划"
        >
          <el-input
            v-model="form.reportExt.nextWeekPlan"
            type="textarea"
            :rows="4"
            maxlength="2000"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="form.remark"
            type="textarea"
            :rows="2"
            maxlength="512"
          />
        </el-form-item>
      </el-form>

      <TaskReportForm
        v-if="isTaskReport"
        ref="taskReportForm"
        :value="form.reportExt"
        @change="handleTaskReportExtChange"
      />
    </div>

    <div slot="footer">
      <el-button
        size="small"
        @click="$emit('update:visible', false)"
      >
        取消
      </el-button>
      <el-button
        type="warning"
        plain
        size="small"
        :loading="currentAction === 'draft'"
        @click="handleSaveDraft"
      >
        保存草稿
      </el-button>
      <el-button
        type="primary"
        plain
        size="small"
        :loading="currentAction === 'preview'"
        @click="handleSaveAndPreview"
      >
        保存并预览
      </el-button>
      <el-button
        type="success"
        size="small"
        :loading="currentAction === 'archive'"
        @click="handleConfirmArchive"
      >
        确认归档
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { workReportService } from './work-report.service.js';
import { formatDate } from '@/features/common/utils/dateTimeUtil.js';
import { downloadBlobAsFile, downloadFromApiBlobResponse } from '@/features/common/utils/downloadFileUtil.js';
import {
  formatDateToYmd,
  getMonthlyReportMonthPickerOptions,
  getWeeklyAnchorDatePickerOptions,
  sundayToSaturdayRange,
  validateWorkReportPeriodHistorical
} from './workReportPeriodUtil.js';
import ImageUploader from '@/features/components/common-image/ImageUploader.vue';
import TaskReportForm from './taskReportExportDialog.vue';

const createSummaryExt = () => ({ schemaVersion: 1, workImages: [], patrolSummary: '', nextWeekPlan: '' });

export default {
  name: 'WorkReportSaveDialog',
  components: { ImageUploader, TaskReportForm },
  props: {
    visible: { type: Boolean, default: false },
    reportId: { type: String, default: null }
  },
  data () {
    return {
      detailLoading: false,
      currentAction: '',
      pendingUuid: null,
      form: {
        reportType: 'monthly',
        periodMonth: null,
        weekAnchorDate: null,
        periodStart: null,
        periodEnd: null,
        remark: '',
        reportExt: createSummaryExt()
      },
      summaryRules: {
        periodMonth: [{ validator: this.validateMonth, trigger: 'change' }],
        weekAnchorDate: [{ validator: this.validateWeek, trigger: 'change' }]
      }
    };
  },
  computed: {
    isTaskReport () {
      return this.form.reportType === 'task';
    },
    isWorkSummary () {
      return !this.isTaskReport;
    },
    typeLocked () {
      return !!(this.reportId || this.pendingUuid);
    },
    dialogTitle () {
      return this.reportId || this.pendingUuid ? '编辑工作报告草稿' : '新建工作报告';
    },
    weeklyRangeHint () {
      return this.form.periodStart && this.form.periodEnd
        ? `报告范围：${this.form.periodStart} ~ ${this.form.periodEnd}` : '';
    },
    monthPickerOptions () {
      return getMonthlyReportMonthPickerOptions();
    },
    weeklyPickerOptions () {
      return getWeeklyAnchorDatePickerOptions();
    }
  },
  methods: {
    validateMonth (rule, value, callback) {
      if (this.form.reportType !== 'monthly' || (value && this.form.periodStart && this.form.periodEnd)) callback();
      else callback(new Error('请选择报告月份'));
    },
    validateWeek (rule, value, callback) {
      if (this.form.reportType !== 'weekly' || (value && this.form.periodStart && this.form.periodEnd)) callback();
      else callback(new Error('请选择周报周次'));
    },
    handleReportTypeChange () {
      this.form.periodMonth = null;
      this.form.weekAnchorDate = null;
      this.form.periodStart = null;
      this.form.periodEnd = null;
      this.form.reportExt = this.isTaskReport ? {} : createSummaryExt();
      this.$nextTick(() => this.$refs.summaryForm && this.$refs.summaryForm.clearValidate());
    },
    handleMonthChange (value) {
      if (!value) {
        this.form.periodStart = null;
        this.form.periodEnd = null;
        return;
      }
      const parts = value.split('-');
      const first = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
      const last = new Date(Number(parts[0]), Number(parts[1]), 0);
      this.form.periodStart = formatDateToYmd(first);
      this.form.periodEnd = formatDateToYmd(last);
    },
    handleWeekAnchorChange (value) {
      if (!value) {
        this.form.periodStart = null;
        this.form.periodEnd = null;
        return;
      }
      const range = sundayToSaturdayRange(value);
      this.form.periodStart = range[0];
      this.form.periodEnd = range[1];
    },
    validateCurrentForm () {
      if (this.isTaskReport) return this.$refs.taskReportForm.validateForm();
      return new Promise(resolve => this.$refs.summaryForm.validate(valid => resolve(valid)));
    },
    validateBusinessPeriod () {
      if (this.isTaskReport) return '';
      return validateWorkReportPeriodHistorical({
        reportType: this.form.reportType,
        periodStart: this.form.periodStart,
        weekAnchorDate: this.form.weekAnchorDate,
        periodRange: [this.form.periodStart, this.form.periodEnd]
      });
    },
    buildSaveParams () {
      const reportExt = this.isTaskReport
        ? this.$refs.taskReportForm.getReportExt()
        : { ...this.form.reportExt, schemaVersion: 1 };
      // 子表单的月份、年份属于 reportExt；先回写父表单，确保保存后的再次编辑使用最新快照。
      this.form.reportExt = { ...reportExt };
      return {
        uuid: this.reportId || this.pendingUuid || undefined,
        reportType: this.form.reportType,
        periodStart: this.form.periodStart,
        periodEnd: this.form.periodEnd,
        remark: this.form.remark || null,
        reportExt
      };
    },
    /** 接收任务明细表单的筛选条件变化，保持父子表单使用同一份报告扩展快照。 */
    handleTaskReportExtChange (reportExt) {
      this.form.reportExt = { ...reportExt };
    },
    async saveCurrentDraft () {
      const valid = await this.validateCurrentForm();
      if (!valid) return null;
      const businessMessage = this.validateBusinessPeriod();
      if (businessMessage) {
        this.$message.error(businessMessage);
        return null;
      }
      const res = await workReportService.save(this.buildSaveParams());
      this.pendingUuid = this.pendingUuid || res.data;
      return this.pendingUuid;
    },
    buildPreviewFilename () {
      if (this.isTaskReport) {
        const taskReportForm = this.$refs.taskReportForm;
        const reportExt = taskReportForm.getReportExt();
        const taskGroup = taskReportForm.selectedTaskGroup;
        const taskGroupName = ((taskGroup && taskGroup.groupName) || '任务工作报告').replace(/\+/g, '').replace(/[\\/:*?"<>|]/g, '_');
        if (reportExt.reportPeriodType === 'year' && reportExt.reportPeriodMonth) {
          return `${taskGroupName}-${reportExt.reportPeriodMonth}月.xlsx`;
        }
        return `${taskGroupName}.xlsx`;
      }
      const typeName = this.form.reportType === 'weekly' ? '周报' : '月报';
      return `工作${typeName}-${(this.form.periodStart || '').replace(/-/g, '')}预览.docx`;
    },
    async handleSaveDraft () {
      this.currentAction = 'draft';
      try {
        const id = await this.saveCurrentDraft();
        if (!id) return;
        this.$message.success('草稿保存成功');
        this.$emit('success');
      } finally {
        this.currentAction = '';
      }
    },
    async handleSaveAndPreview () {
      this.currentAction = 'preview';
      try {
        const id = await this.saveCurrentDraft();
        if (!id) return;
        const res = await workReportService.previewReportFile(id);
        if (this.isTaskReport) {
          downloadBlobAsFile(res.data, this.buildPreviewFilename());
          this.$message.success('预览文件已下载');
          return;
        }
        downloadFromApiBlobResponse(res, this.buildPreviewFilename());
        this.$message.success('预览文件已下载');
      } finally {
        this.currentAction = '';
      }
    },
    async handleConfirmArchive () {
      try {
        await this.$confirm('归档后报告不可再修改或删除报告，确认归档吗？', '确认归档', { type: 'warning' });
      } catch (error) {
        return;
      }
      this.currentAction = 'archive';
      try {
        const id = await this.saveCurrentDraft();
        if (!id) return;
        await workReportService.archive(id);
        this.$message.success('归档成功');
        this.$emit('success');
        this.$emit('update:visible', false);
      } finally {
        this.currentAction = '';
      }
    },
    async loadDetail () {
      this.detailLoading = true;
      try {
        const res = await workReportService.getDetail(this.reportId);
        const record = res.data || {};
        this.form.reportType = record.reportType || 'monthly';
        this.form.periodStart = formatDate(record.periodStart) === '-' ? null : formatDate(record.periodStart);
        this.form.periodEnd = formatDate(record.periodEnd) === '-' ? null : formatDate(record.periodEnd);
        this.form.remark = record.remark || '';
        this.form.reportExt = record.reportExt || (this.form.reportType === 'task' ? {} : createSummaryExt());
        if (this.form.reportType === 'monthly' && this.form.periodStart) this.form.periodMonth = this.form.periodStart.slice(0, 7);
        if (this.form.reportType === 'weekly') this.form.weekAnchorDate = this.form.periodStart;
      } finally {
        this.detailLoading = false;
      }
    }
  },
  created () {
    if (this.reportId) this.loadDetail();
  }
};
</script>

<style lang="scss" scoped>
.report-type-form,
.el-form {
  padding: 10px 20px 0;
}

.field-hint {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}
</style>
